// AGC: AdvisorMarketplace - Main marketplace screen for browsing consultants
// AGC: Premium-only feature with search and filters

import React, { useState, useEffect } from 'react'
import { 
  Search, 
  Filter, 
  Crown, 
  Lock,
  RefreshCw,
  Users,
  X,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { ConsultantCard } from './ConsultantCard'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type ConsultantProfile, 
  type Specialization,
  SPECIALIZATION_INFO,
} from '../../types/advisor'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'

interface AdvisorMarketplaceProps {
  onNavigate: (screen: Screen, params?: Record<string, unknown>) => void
  onBack: () => void
}

const BACKEND_URL = getBackendUrl()

/**
 * AGC: Advisor Marketplace Component
 */
export const AdvisorMarketplace: React.FC<AdvisorMarketplaceProps> = ({
  onNavigate,
  onBack,
}) => {
  const { t } = useTranslation()
  const { tier, canUseFeature, openUpgradeModal } = useSubscription()
  
  const hasAccess = canUseFeature('hasHumanAdvisor')

  const [consultants, setConsultants] = useState<ConsultantProfile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedSpecializations, setSelectedSpecializations] = useState<Specialization[]>([])
  const [minRating, setMinRating] = useState<number>(0)

  /**
   * AGC: Fetch consultants from backend
   */
  const fetchConsultants = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      if (searchQuery) params.append('searchQuery', searchQuery)
      if (selectedSpecializations.length > 0) {
        params.append('specializations', selectedSpecializations.join(','))
      }
      if (minRating > 0) params.append('minRating', String(minRating))
      
      const response = await fetch(`${BACKEND_URL}/api/v1/advisors?${params}`)
      const data = await response.json()
      
      if (data.success) {
        setConsultants(data.consultants)
      } else {
        setError(data.error || 'Erro ao carregar consultores')
      }
    } catch (err) {
      console.error('Error fetching consultants:', err)
      setError('Erro de conexão')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (hasAccess) {
      fetchConsultants()
    }
  }, [hasAccess, selectedSpecializations, minRating])

  /**
   * AGC: Handle search
   */
  const handleSearch = () => {
    fetchConsultants()
  }

  /**
   * AGC: Toggle specialization filter
   */
  const toggleSpecialization = (spec: Specialization) => {
    setSelectedSpecializations(prev => 
      prev.includes(spec) 
        ? prev.filter(s => s !== spec)
        : [...prev, spec]
    )
  }

  /**
   * AGC: View consultant profile
   */
  const handleViewProfile = (consultantId: string) => {
    onNavigate('consultant-profile' as Screen, { consultantId })
  }

  // AGC: Show premium gate if not premium
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
        <TopBar
          title={t('advisor.marketplace') || 'Consultor Humano'}
          onBack={onBack}
        />
        
        <div className="p-4 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {t('advisor.premiumOnly') || 'Recurso Premium'}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('advisor.premiumDescription') || 
                'O Consultor Humano é exclusivo para assinantes Premium. Tenha acesso a agrônomos certificados para te ajudar nas decisões.'
              }
            </p>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Crown className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                <span className="font-semibold text-yellow-700 dark:text-yellow-300">
                  Premium - R$ 299/mês
                </span>
              </div>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                <li>✓ Acesso ao marketplace de consultores</li>
                <li>✓ Consultas com agrônomos certificados</li>
                <li>✓ Diagnósticos, planos e prescrições</li>
                <li>✓ Suporte prioritário</li>
              </ul>
            </div>
            
            <button
              onClick={() => openUpgradeModal('hectares')}
              className="w-full py-3 px-6 bg-gradient-to-r from-yellow-500 to-amber-500 text-white rounded-xl font-semibold hover:from-yellow-600 hover:to-amber-600 transition-all shadow-lg shadow-yellow-500/25"
            >
              Fazer Upgrade para Premium
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('advisor.marketplace') || 'Consultor Humano'}
        onBack={onBack}
        actions={
          <button 
            onClick={fetchConsultants}
            disabled={isLoading}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* AGC: Search Bar */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('advisor.searchPlaceholder') || 'Buscar consultores...'}
              className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-slate-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-3 rounded-xl transition-colors ${
              showFilters || selectedSpecializations.length > 0
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* AGC: Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-700 dark:text-gray-300">
                {t('advisor.specializations') || 'Especializações'}
              </h4>
              {selectedSpecializations.length > 0 && (
                <button
                  onClick={() => setSelectedSpecializations([])}
                  className="text-sm text-emerald-600 dark:text-emerald-400"
                >
                  Limpar
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(SPECIALIZATION_INFO) as Specialization[]).slice(0, 8).map(spec => {
                const info = SPECIALIZATION_INFO[spec]
                const isSelected = selectedSpecializations.includes(spec)
                return (
                  <button
                    key={spec}
                    onClick={() => toggleSpecialization(spec)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-500'
                    }`}
                  >
                    {info.icon} {info.name}
                  </button>
                )
              })}
            </div>

            {/* AGC: Rating filter */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('advisor.minRating') || 'Avaliação Mínima'}
              </h4>
              <div className="flex gap-2">
                {[0, 4.0, 4.5, 4.8].map(rating => (
                  <button
                    key={rating}
                    onClick={() => setMinRating(rating)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      minRating === rating
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {rating === 0 ? 'Todos' : `${rating}+ ⭐`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AGC: Active filters */}
        {selectedSpecializations.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedSpecializations.map(spec => (
              <span
                key={spec}
                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm"
              >
                {SPECIALIZATION_INFO[spec].icon} {SPECIALIZATION_INFO[spec].name}
                <button onClick={() => toggleSpecialization(spec)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* AGC: Content */}
      <div className="p-4 pb-24">
        {/* AGC: Stats header */}
        <div className="flex items-center gap-2 mb-4 text-gray-500 dark:text-gray-400">
          <Users className="w-4 h-4" />
          <span className="text-sm">
            {consultants.length} {t('advisor.consultantsFound') || 'consultores encontrados'}
          </span>
        </div>

        {/* AGC: Loading state */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-4 animate-pulse">
                <div className="h-20 bg-gray-200 dark:bg-slate-700 rounded-xl mb-4" />
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* AGC: Error state */}
        {error && !isLoading && (
          <GlassCard>
            <div className="text-center py-6">
              <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchConsultants}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg"
              >
                Tentar novamente
              </button>
            </div>
          </GlassCard>
        )}

        {/* AGC: Empty state */}
        {!isLoading && !error && consultants.length === 0 && (
          <GlassCard>
            <div className="text-center py-6">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('advisor.noConsultantsFound') || 'Nenhum consultor encontrado'}
              </p>
              {selectedSpecializations.length > 0 && (
                <button
                  onClick={() => setSelectedSpecializations([])}
                  className="mt-4 text-emerald-600 dark:text-emerald-400"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </GlassCard>
        )}

        {/* AGC: Consultants grid */}
        {!isLoading && !error && consultants.length > 0 && (
          <div className="space-y-4">
            {consultants.map(consultant => (
              <ConsultantCard
                key={consultant.id}
                consultant={consultant}
                onViewProfile={handleViewProfile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdvisorMarketplace

